import op from 'object-path';
import { mapGetters } from 'vuex';
import { checkForMeasurementError } from '@/modules/utils';

// 有些尺寸可以在 MTM 上選擇 A/B/C，這裡定義哪些尺寸有這個選項
// 請注意：如果使用 App config 自定義尺寸的話，則不會自動切換 A/B/C，而是直接使用 customMeasurement
const MEASUREMENTS = [
  { key: 'Collar Circumference', nameKey: 'neck', optionKeys: ['Collar Circumference B'] },
  { key: 'Chest Circumference', nameKey: 'chest' },
  {
    key: 'Center Back Neck to Crotch Length',
    nameKey: 'torso',
    optionKeys: ['Center Back Neck to Crotch Length B', 'Center Back Neck to Crotch Length C'],
  },
  { key: 'Narrow Waist Circumference', nameKey: 'mid_waist' },
  { key: 'Low Hip Circumference', nameKey: 'hip' },
  {
    key: 'Back Cross Shoulder Width',
    nameKey: 'shoulder',
    optionKeys: ['Back Cross Shoulder Width B', 'Back Cross Shoulder Width C'],
  },
  { key: 'Left Upper Arm Circumference', nameKey: 'biceps' },
  { key: 'Left Arm Length', nameKey: 'arm' },
  { key: 'Left Thigh Circumference', nameKey: 'thigh' },
  { key: 'Left Inseam Length', nameKey: 'inner_leg' },
];

const normalizeMeasurementOrigin = measurement => {
  if (!measurement || typeof measurement !== 'object' || Array.isArray(measurement)) {
    return measurement;
  }

  return Object.entries(measurement).reduce((acc, [key, value]) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      acc[key] = value;
      return acc;
    }

    if (Object.prototype.hasOwnProperty.call(value, 'origin')) {
      acc[key] = value;
      return acc;
    }

    acc[key] = { origin: value };
    return acc;
  }, {});
};

const measurements = {
  beforeDestroy() {
    this.stopAutoplay();
  },
  data: () => ({
    useInch: false,
    activeMeasurementIndex: -1,
    currentMeasurement: null,
    measurements: [],
    measurementFailed: false,
  }),
  methods: {
    processMeasurements(data, skipError = false, onlyProcess = false, customMeasurement = null) {
      const {
        error,
        measurement,
        alg_ver: algVer,
        size_xt_custom_name: customSizeXtName,
        version,
      } = data;
      const { error: errorI, measurement: measurementPoseI } = data.size_xt_pose_i || {};
      const parsedVersion = Number(version);
      const isLegacyVersion = Number.isFinite(parsedVersion) && parsedVersion < 4;
      const normalizedMeasurement = isLegacyVersion
        ? normalizeMeasurementOrigin(measurement)
        : measurement;
      const normalizedMeasurementPoseI = isLegacyVersion
        ? normalizeMeasurementOrigin(measurementPoseI)
        : measurementPoseI;
      const errors = checkForMeasurementError(error);
      if (errors.length > 0) {
        this.measurementFailed = true;
        if (!skipError) {
          throw new Error(`measurement failed, errors: ${errors.join(', ')}`);
        }
      }
      if (Array.isArray(customMeasurement)) {
        const hasIPose = customMeasurement.filter(m => m.pose === 'I').length;
        const errorsI = checkForMeasurementError(errorI);
        if (hasIPose && errorsI.length > 0) {
          this.measurementFailed = true;
          if (!skipError) {
            throw new Error(`measurement I pose failed, errors: ${errorsI.join(', ')}`);
          }
        }
      }

      const OPTION_MEASUREMENTS = MEASUREMENTS.map(m => {
        if (!m.optionKeys || !customSizeXtName) return m;

        const { key, subKey } = m;
        const invalidKey = [key, ...m.optionKeys].find(sizeKey => {
          const mtmKey = (subKey ? `_${subKey}__of_` : '') + sizeKey.replaceAll(' ', '_');
          const custom = customSizeXtName.find(
            c => c.key === mtmKey && c.pose === 'A' && c.hide === true
          );
          return !custom;
        });

        if (invalidKey && invalidKey !== key) {
          console.log(`[INFO] "${key}" replace with "${invalidKey}"`);
          return { ...m, key: invalidKey, originKey: key };
        }

        return m;
      });
      const target = customMeasurement || OPTION_MEASUREMENTS;
      const selectedMeasurements = target.map(m => {
        const { key, rightKey, pose, ...extra } = m;
        const mtmKey = (m.subKey ? `_${m.subKey}__of_` : '') + key.replaceAll(' ', '_');
        let customName =
          customSizeXtName?.find(c => c.key === mtmKey && c.pose === 'A')?.name?.trim() ?? null;
        // 左右對稱的尺寸由 L / R 標記區分，所以把自訂名尾端的 "(左)" / "(右)" 去掉
        if (rightKey && customName?.endsWith(')')) {
          const lastParenthesisIndex = customName.lastIndexOf('(');
          if (lastParenthesisIndex !== -1) {
            customName = customName.substring(0, lastParenthesisIndex).trim();
          }
        }

        const measure = {
          ...op.get(normalizedMeasurement, `${m.key}.${m.subKey || 'origin'}`, {}),
          ...m,
          algVer,
          customName,
        };
        measure.rightMes = rightKey
          ? {
              ...op.get(normalizedMeasurement, `${rightKey}.${m.subKey || 'origin'}`, {}),
              key: rightKey,
              ...extra,
              algVer,
              // 左右共用左側的自訂名
              customName,
            }
          : null;
        if (measure.rightMes?.view === 'left') measure.rightMes.view = 'right';
        else if (measure.rightMes?.view === 'right') measure.rightMes.view = 'left';

        if (pose === 'I') {
          const valueI = op.get(
            normalizedMeasurementPoseI,
            `${key}.${m.subKey || 'origin'}.value`,
            null
          );

          if (valueI !== null) {
            console.log(`[INFO] "${key}" replace A Pose: ${measure.value} with I Pose: ${valueI}`);
            measure.value = valueI;
          } else {
            console.log(`[WARNING] "${key}" not found in IPose`);
          }

          if (measure.rightMes) {
            const rightValueI = op.get(
              normalizedMeasurementPoseI,
              `${rightKey}.${m.subKey || 'origin'}.value`,
              null
            );
            if (rightValueI !== null) {
              console.log(
                `[INFO] "${rightKey}" replace A Pose: ${measure.rightMes.value} with I Pose: ${rightValueI}`
              );
              measure.rightMes.value = rightValueI;
            } else {
              console.log(`[WARNING] "${rightKey}" not found in IPose`);
            }
          }
        }

        if (!this.isFemale && m.threshold_male) {
          measure.threshold = m.threshold_male;
          if (measure.rightMes) {
            measure.rightMes.threshold = measure.threshold;
          }
        }

        return measure;
      });

      if (this.isFemale) {
        selectedMeasurements.find(m => m.key === 'Chest Circumference').nameKey = 'chest_female';
      }

      // ** Dirty Fix **
      // Waist Hip Ratio from sizext is Narrow Waist Circumference / Low Hip Circumference
      // But for Dailybelle they show it as Thinnest Waist Circumference / Low Hip Circumference...
      if (selectedMeasurements.find(m => m.key === 'Waist Hip Ratio')) {
        const whr = selectedMeasurements.find(m => m.key === 'Waist Hip Ratio');
        const waist = selectedMeasurements.find(m => m.key === 'Thinnest Waist Circumference');
        const hip = selectedMeasurements.find(m => m.key === 'Low Hip Circumference');
        if (waist && hip && waist.value > 0 && hip.value > 0) {
          const ratio = waist.value / hip.value;
          whr.value = Number.isFinite(ratio) ? ratio : null;
          whr.type = 'ratio';
        }
      }

      selectedMeasurements.forEach(m => {
        if (!m.key) {
          console.log(`[ERROR] something wrong with this measurement: ${JSON.stringify(m)}`);
        }
      });
      if (onlyProcess) return selectedMeasurements;
      this.measurements = selectedMeasurements;
      this.$store.commit('user/SET_MEASUREMENTS', selectedMeasurements);
      return null;
    },
    autoplayMeasurements() {
      this.autoplayTimer = setTimeout(() => {
        const total = this.measurements.length;
        if (!total) return;

        this.activeMeasurementIndex =
          this.activeMeasurementIndex >= total - 1 ? 0 : this.activeMeasurementIndex + 1;
        const next = this.measurements[this.activeMeasurementIndex];
        this.setCurrentMeasurement(next);
        this.autoplayMeasurements();
      }, 3000);
    },
    stopAutoplay() {
      if (this.autoplayTimer) {
        clearTimeout(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    },
    onUnitSwitch(useInch) {
      this.useInch = useInch;
    },
    setCurrentMeasurement(measurement) {
      this.currentMeasurement = measurement;
    },
  },
  computed: {
    ...mapGetters('user', ['isMale', 'isFemale']),
    leftMeasurements() {
      return this.measurements
        .slice(0, 5)
        .map(m => ({ ...m, name: this.$t(`measurement.${m.nameKey}`) }));
    },
    rightMeasurements() {
      return this.measurements
        .slice(5, 10)
        .map(m => ({ ...m, name: this.$t(`measurement.${m.nameKey}`) }));
    },
    hasMeasurements() {
      return this.measurements.length > 0;
    },
  },
};

export default measurements;
